/**
 * RFC 8785 JSON Canonicalization Scheme + SHA-256, used for every content hash the system
 * signs or chains (PROJECT_SPEC.md §5.4 "do the canonicalization in the application",
 * §5.6 payload binding, §9.2 record.json). Runs in browser and Node (WebCrypto).
 */
import { canonicalize } from "json-canonicalize";

export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

export function canonicalJson(value: Json): string {
  return canonicalize(value);
}

export function canonicalBytes(value: Json): Uint8Array {
  return new TextEncoder().encode(canonicalJson(value));
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes.slice());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** sha256(JCS(value)) as lowercase hex — the `payload_jcs` written next to every batch_event. */
export async function hashCanonical(value: Json): Promise<string> {
  return sha256Hex(canonicalBytes(value));
}

export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}
